import * as constants from '../../../common/constants';
import env from '../../../common/env';

export const isRplusEpubTitleType = (titleType) => titleType === constants.RPLUS_EPUB_TITLE;

export const hasRplusEpubPreviewContent = (epubAsset) => Boolean(epubAsset?.slates?.length);

export const isPreviewAssetForBook = (store, bookid) =>
  store?.bookId != null && String(store.bookId) === String(bookid);

export const isPreviewTocLoadedForBook = (store, bookid, tocStatus, hasPreviewContent) => {
  if (!isPreviewAssetForBook(store, bookid) || tocStatus?.isPending) {
    return false;
  }
  if (tocStatus?.isError) {
    return hasPreviewContent(store);
  }
  return true;
};

export const shouldSkipPreviewTocFetch = (store, bookid, tocStatus, tocFetchKey, cachedFetchKey, hasPreviewContent) => {
  if (cachedFetchKey !== tocFetchKey || !isPreviewTocLoadedForBook(store, bookid, tocStatus, hasPreviewContent)) {
    return false;
  }
  return hasPreviewContent(store) || !tocStatus?.isError;
};

export const isPreviewTocFetchInFlightForBook = (store, bookid, tocStatus) =>
  Boolean(tocStatus?.isPending) && isPreviewAssetForBook(store, bookid);

export const didPreviewTocFetchComplete = (prevStatus, nextStatus, isRelevantTitleType) =>
  Boolean(prevStatus?.isPending && !nextStatus?.isPending && isRelevantTitleType);

export const isPreviewStoreOutOfSync = (store, bookid, prevStore, hasPreviewContent) =>
  !isPreviewAssetForBook(store, bookid) || (hasPreviewContent(prevStore) && !hasPreviewContent(store));

export const isRplusEpubTocReadyToFetch = (bookDetail, bookid) =>
  isRplusEpubTitleType(bookDetail?.titleType) &&
  String(bookDetail?.bookID) === String(bookid) &&
  Boolean(bookDetail?.basePath);

// assettoc response is for the book we're still viewing
export const isRplusAssettocActiveForBook = (bookDetail, bookid) =>
  isRplusEpubTitleType(bookDetail?.titleType) && bookid != null && String(bookDetail?.bookID) === String(bookid);

export const isRplusEpubTocLoadedForBook = (epubAsset, bookid, epubTocStatus) =>
  isPreviewTocLoadedForBook(epubAsset, bookid, epubTocStatus, hasRplusEpubPreviewContent);

export const shouldShowRplusEpubBookDetailPreview = (epubAsset, bookid, epubTocStatus) =>
  isRplusEpubTocLoadedForBook(epubAsset, bookid, epubTocStatus) && hasRplusEpubPreviewContent(epubAsset);

export const getRplusEpubTocFetchKey = (bookid, basePath) => `${bookid}:${basePath || ''}`;

export const shouldSkipRplusAssettocFetch = (epubAsset, bookid, epubTocStatus, tocFetchKey, cachedFetchKey) =>
  shouldSkipPreviewTocFetch(epubAsset, bookid, epubTocStatus, tocFetchKey, cachedFetchKey, hasRplusEpubPreviewContent);

// Book Detail passes cachedFetchKey; preview route does not.
export const loadRplusEpubAssettocIfNeeded = (epubAsset, bookDetail, bookid, epubTocStatus, cachedFetchKey = null) => {
  if (
    !isRplusEpubTocReadyToFetch(bookDetail, bookid) ||
    isPreviewTocFetchInFlightForBook(epubAsset, bookid, epubTocStatus)
  ) {
    return cachedFetchKey;
  }

  const tocFetchKey = getRplusEpubTocFetchKey(bookid, bookDetail.basePath);

  if (shouldSkipRplusAssettocFetch(epubAsset, bookid, epubTocStatus, tocFetchKey, cachedFetchKey)) {
    return cachedFetchKey;
  }

  if (isPreviewAssetForBook(epubAsset, bookid) && hasRplusEpubPreviewContent(epubAsset)) {
    return tocFetchKey;
  }

  epubAsset.fetch(bookid);
  return tocFetchKey;
};

export const getBookFolderPath = (bookBasePath) => {
  if (!bookBasePath) {
    return '';
  }

  let assetPath = bookBasePath;
  const pathMatch = bookBasePath.match(/[?&]path=([^&]+)/i);
  if (pathMatch?.[1]) {
    assetPath = decodeURIComponent(pathMatch[1]);
  }

  return assetPath.replace(/^\/+/, '').replace(/\/+$/, '');
};

const isAbsoluteUrl = (value) => typeof value === 'string' && /^https?:\/\//i.test(value);

export const getPxeBasePath = (bookBasePath) => {
  const assetPath = getBookFolderPath(bookBasePath);
  if (!assetPath) {
    return '';
  }
  const cloudfrontBase = (env.PXE_CLOUDFRONT_BASE || '').replace(/\/+$/, '');
  return `${cloudfrontBase}/${assetPath}/`;
};

const buildCloudfrontPageUrl = (bookFolderPath, relativeUri) => {
  if (relativeUri == null || relativeUri === '') {
    const cloudfrontBase = (env.PXE_CLOUDFRONT_BASE || '').replace(/\/+$/, '');
    const folder = (bookFolderPath || '').replace(/^\/+/, '').replace(/\/+$/, '');
    return folder ? `${cloudfrontBase}/${folder}/` : `${cloudfrontBase}/`;
  }
  if (isAbsoluteUrl(relativeUri)) {
    return relativeUri;
  }
  const cloudfrontBase = (env.PXE_CLOUDFRONT_BASE || '').replace(/\/+$/, '');
  const folder = (bookFolderPath || '').replace(/^\/+/, '').replace(/\/+$/, '');
  const uri = String(relativeUri).replace(/^\/+/, '');
  const combined = folder ? `${cloudfrontBase}/${folder}/${uri}` : `${cloudfrontBase}/${uri}`;
  return combined.replace(/([^:]\/)\/+/g, '$1');
};

const prefixTocNodeUrls = (nodes, basePath) => {
  if (!Array.isArray(nodes)) {
    return nodes;
  }
  return nodes.map((node) => {
    const next = { ...node };
    // Keep href relative for VegaReader id matching; only prefix url for resolved links.
    if (next.url && !isAbsoluteUrl(next.url)) {
      next.url = `${basePath}${String(next.url).replace(/^\/+/, '')}`;
    }
    if (next.children?.length) {
      next.children = prefixTocNodeUrls(next.children, basePath);
    }
    return next;
  });
};

// CloudFront + basePath for assettoc hrefs
export const adaptRplusEpubAsset = (assetPayload, bookBasePath) => {
  if (!assetPayload || assetPayload.contentType !== constants.RPLUS_EPUB_TITLE) {
    return assetPayload;
  }

  const basePath = bookBasePath
    ? getPxeBasePath(bookBasePath)
    : buildCloudfrontPageUrl(assetPayload.basePath || '', '');

  const slates = (assetPayload.slates || []).map((slate) => {
    const relativeUri = slate.uri || slate.href;
    return {
      ...slate,
      uri: isAbsoluteUrl(relativeUri) ? relativeUri : `${basePath}${String(relativeUri).replace(/^\/+/, '')}`
    };
  });

  const tocData = assetPayload.tocData
    ? {
        ...assetPayload.tocData,
        children: prefixTocNodeUrls(assetPayload.tocData.children, basePath)
      }
    : assetPayload.tocData;

  return {
    ...assetPayload,
    basePath,
    slates,
    tocData
  };
};

const compareRplusPlayOrder = (left, right) => Number(left.playOrder) - Number(right.playOrder);

const normalizeContentPath = (value) => {
  if (value == null || value === '') {
    return '';
  }
  const withoutQuery = String(value).split('?')[0];
  return withoutQuery.replace(/^https?:\/\/[^/]+/i, '').replace(/^\/+/, '');
};

const contentPathsMatch = (left, right) => {
  const normalizedLeft = normalizeContentPath(left);
  const normalizedRight = normalizeContentPath(right);
  if (!normalizedLeft || !normalizedRight) {
    return false;
  }
  return (
    normalizedLeft === normalizedRight ||
    normalizedLeft.endsWith(normalizedRight) ||
    normalizedRight.endsWith(normalizedLeft)
  );
};

const resolveRplusSlatePlayOrder = (slate, idx) => {
  if (slate?.playOrder != null) {
    return slate.playOrder;
  }
  if (slate?.index != null) {
    return slate.index + 1;
  }
  return idx + 1;
};

const normalizeRelativeHref = (value) => {
  if (value == null || value === '') {
    return '';
  }
  if (isAbsoluteUrl(value)) {
    return normalizeContentPath(value);
  }
  return String(value).replace(/\\/g, '/').replace(/^\/+/, '');
};

const getTocEntryLabel = (entry) => {
  if (entry?.label != null && String(entry.label).trim() !== '') {
    return String(entry.label).trim();
  }
  if (entry?.title != null && String(entry.title).trim() !== '') {
    return String(entry.title).trim();
  }
  return '';
};

// sort by playOrder so cover (1) is playlist index 0
export const mapRplusSlatesForPxeReader = (slates = []) =>
  (slates || [])
    .map((slate, idx) => {
      const playOrder = resolveRplusSlatePlayOrder(slate, idx);
      const href = normalizeRelativeHref(slate.href || slate.uri);
      const slateId = slate.id != null && String(slate.id).trim() !== '' ? String(slate.id).trim() : String(playOrder);
      return {
        chapterId: null,
        chapterTitle: null,
        href,
        id: slateId,
        index: slate.index != null ? slate.index : idx,
        pagenumber: slate.pagenumber ?? null,
        parentid: null,
        playOrder,
        printDisabled: null,
        title: slate.title,
        type: slate.type || 'page',
        uri: slate.uri,
        linkUri: href
      };
    })
    .sort(compareRplusPlayOrder);

export const getFirstRplusEpubPageId = (epubAsset) => {
  const pagePlaylist = mapRplusSlatesForPxeReader(epubAsset?.slates);
  if (!pagePlaylist.length) {
    return undefined;
  }
  return pagePlaylist[0].playOrder;
};

const enrichRplusTocEntries = (entries, pagePlaylist) =>
  entries.map((entry) => {
    const href = normalizeRelativeHref(entry.href || entry.url || entry.uri);
    const matched = pagePlaylist.find(
      (page) => contentPathsMatch(page.href, href) || contentPathsMatch(page.uri, href)
    );
    const playOrder = matched?.playOrder ?? entry.playOrder;
    const resolvedHref = matched?.href ?? href;
    const label = getTocEntryLabel(entry);
    const resolvedId =
      matched?.id ?? (entry.id != null && String(entry.id).trim() !== '' ? String(entry.id).trim() : String(playOrder));
    return {
      ...entry,
      id: resolvedId,
      href: resolvedHref,
      linkUri: resolvedHref,
      url: matched?.uri ?? entry.url ?? entry.uri,
      uri: matched?.uri ?? entry.uri ?? entry.url,
      title: label || entry.title,
      label: label || entry.label,
      playOrder,
      children: entry.children?.length ? enrichRplusTocEntries(entry.children, pagePlaylist) : []
    };
  });

export const mapRplusTocForPxeReader = (tocData, pagePlaylist = []) => ({
  ...(tocData || {}),
  children: enrichRplusTocEntries(tocData?.children ?? [], pagePlaylist)
});

export const buildRplusEpubReaderPayload = (epubAsset) => {
  const pagePlaylist = mapRplusSlatesForPxeReader(epubAsset?.slates);
  return {
    pagePlaylist,
    tocData: mapRplusTocForPxeReader(epubAsset?.tocData, pagePlaylist)
  };
};

export const buildRplusEpubLayoutBookInfo = (epubAsset) => ({
  isDoublePage: false,
  isRightToLeft: false,
  isFixedLayout: epubAsset?.isFixedLayout ?? true,
  layoutDimensions: {
    height: epubAsset?.layoutDimensions?.height ?? 2000,
    width: epubAsset?.layoutDimensions?.width ?? 1587,
    rightStart: false
  },
  preferenceObject: epubAsset?.preferenceObject ?? { sliderVal: '100', theme: 'White' },
  printPageNumber: epubAsset?.printPageNumber ?? null,
  productModel: 'RPLUS_EPUB'
});

/** Normalized playlist for VegaReader (navigation, resources, legacy EPUB helpers). */
export const getRplusEpubPagePlaylist = (epubAsset) =>
  epubAsset?.slates?.length ? mapRplusSlatesForPxeReader(epubAsset.slates) : [];

/** Full reader payload when assettoc is loaded; null if no slates yet. */
export const getRplusEpubReaderPayload = (epubAsset) =>
  epubAsset?.slates?.length ? buildRplusEpubReaderPayload(epubAsset) : null;

/** Page playlist, TOC, and layout metadata for VegaReader in one call. */
export const buildRplusEpubVegaView = (epubAsset, isReady) => {
  const layoutBookInfo = buildRplusEpubLayoutBookInfo(epubAsset);
  if (!isReady || !epubAsset?.slates?.length) {
    return { pagePlaylist: [], tocData: {}, layoutBookInfo };
  }
  const { pagePlaylist, tocData } = buildRplusEpubReaderPayload(epubAsset);
  return { pagePlaylist, tocData, layoutBookInfo };
};
